import * as React from "react";
import Title from "../../../../../common/title/title";
import Button from "../../../../../common/button/button";
import SpecItem from "../../../../../common/spec_item/spec_item";
import { DroneDTO } from "../../../../../store/drone_slice";

interface SpecRow {
  id: number;
  keyName: string;
  value: string;
}

interface StepProps {
  droneData: Pick<DroneDTO, "specs">;
  updateDroneData: (newData: Partial<Pick<DroneDTO, "specs">>) => void;
}

const options = [
  "Camera",
  "Max Flight Time",
  "Max Speed",
  "Transmission Range",
  "Weight",
  "Gimbal",
];

const DroneSpecsForm: React.FC<StepProps> = ({
  droneData,
  updateDroneData,
}) => {
  const convertSpecsToObjectArray = (specsObj: { [key: string]: string }): SpecRow[] => {
    return Object.entries(specsObj).map(([key, value], index) => ({
      id: Date.now() + index,
      keyName: key,
      value: value,
    }));
  };

  const [specRows, setSpecRows] = React.useState<SpecRow[]>(() => {
    const initialRows = convertSpecsToObjectArray(droneData.specs || {});
    return initialRows.length > 0 ? initialRows : [{ id: Date.now(), keyName: "", value: "" }];
  });

  const syncSpecsObject = (currentRows: SpecRow[]) => {
    const specsObject: { [key: string]: string } = {};
    currentRows.forEach(row => {
      if (row.keyName) {
        specsObject[row.keyName] = row.value;
      }
    });
    updateDroneData({ specs: specsObject });
  };

  const handleSpecChange = (
    id: number,
    field: "keyName" | "value",
    newValue: string,
  ) => {
    const updatedRows = specRows.map(row =>
      row.id === id ? { ...row, [field]: newValue } : row
    );
    setSpecRows(updatedRows);
    syncSpecsObject(updatedRows);
  };

  const handleAddSpec = () => {
    if (specRows.length > 0 && !specRows[specRows.length - 1]?.keyName) {
       return;
    }
    setSpecRows([...specRows, { id: Date.now(), keyName: "", value: "" }]);
  };

  const handleDeleteSpec = (idToDelete: number) => {
    if (specRows.length <= 1) return; 

    const updatedRows = specRows.filter(row => row.id !== idToDelete);
    setSpecRows(updatedRows);
    syncSpecsObject(updatedRows);
  };

  return (
    <div className="max-w-2xl mx-auto mt-8">
      <Title
        title="Offer some drone specs"
        subtitle="Act like a PRO by offering technical information"
      />
      <div className="space-y-1 mb-4">
        {specRows.map((row) => (
          <SpecItem
            key={row.id}
            value={row.keyName}
            description={row.value}
            onValueChange={(newKeyName) => handleSpecChange(row.id, "keyName", newKeyName)}
            onDescriptionChange={(newValue) => handleSpecChange(row.id, "value", newValue)}
            onDelete={() => handleDeleteSpec(row.id)}
            options={options}
            placeholder="Select Specification"
          />
        ))}
      </div>
      <Button onClick={handleAddSpec} buttonType="secondary">
        Add Specification
      </Button>
    </div>
  );
};

export default DroneSpecsForm;
